import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { CDN_ROOT } from '~/core/core.module';
import {
  Component,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityFileTypeEnum,
  OrganizationLightInterface,
  ReviewInterface,
  ReviewStatInterface,
  SimpleStatRatingInterface,
} from '@sport-activities/sdk';
import { Subscription, take } from 'rxjs';
import diacritic from 'diacritic';
import { serializer } from '~/core/helpers/url/serializer/serializer';
import { TimeslotListVariantEnum } from '~/component/activity-timeslot-list/enums/timeslot-list-variant.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { MapsService } from '~/core/services/maps/maps.service';
import { MessagingService } from '~/messaging/services/messaging.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';

import { RatingSizeEnum } from '~/component/vtmn-rating/enums/RatingSize.enum';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { VtmnPlayIconTypeEnum } from '../vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '../buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { RedirectionService } from '~/core/services/redirection/redirection.service';

@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ActivityComponent implements OnInit {
  @Input() public activity: Activity;
  @Input() public isPreview? = false;
  @Input() public organization: OrganizationLightInterface;
  @Input() public organizationReviewsStats: ReviewStatInterface;
  @Input() public timeslots: Timeslot[] = [];

  private readonly subscriptions = new Subscription();
  public isMobileDevice: boolean;
  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly timeslotVariant: TimeslotListVariantEnum =
    TimeslotListVariantEnum.SHORT;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public averageAttributeRating: SimpleStatRatingInterface[];
  public averageRating: number;
  public isOpen = false;
  public hasReview: boolean;
  public hasScrolled = false;
  public organizerReviewsSize: RatingSizeEnum = RatingSizeEnum.SMALL;
  public recommendationRate: number;
  public review: ReviewInterface;
  public reviewCount: number;
  public reviews: ReviewInterface[];
  public stats: ReviewStatInterface;
  public isBms: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(CDN_ROOT) private readonly cdnRoot: string,
    private readonly mapsService: MapsService,
    private readonly translateService: TranslateService,
    private readonly activityService: ActivityService,
    private readonly messagingService: MessagingService,
    protected readonly paramethorService: ParamethorService,
    private readonly matchMediaService: MatchMediaService,
    private readonly viewportScroller: ViewportScroller,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly redirectionService: RedirectionService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    if (!this.activity?.isLive) {
      this.mapsService.initMapScript();
      this.initMap();
    }

    if (this.activity?.isBms) {
      this.isBms = true;
    }
  }

  public get accountIcon(): VtmnPlayIconTypeEnum {
    return VtmnPlayIconTypeEnum.User;
  }

  public get defaultPicture() {
    let defaultImage: string;
    if (this.activity?.image?.url?.includes('default.jpg')) {
      const defaultUrl = this.isMobileDevice ? '-384x256.jpg' : '-618x412.jpg';
      defaultImage = `${this.cdnRoot}/common/default${defaultUrl}`;
    } else {
      defaultImage = this.activity?.image?.url;
    }
    return defaultImage;
  }

  public get hasMultipleOffers(): boolean {
    return Boolean(this.activity?.indicativeOffers?.length > 1);
  }

  public get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  public get isShortDescription(): boolean {
    return this.organization?.description?.length <= 150;
  }

  private readonly removeEmoji = (url: string) => {
    return url?.replace(/[^\x20-\x7F]+/g, '');
  };

  public get organizationNameFormatted(): string {
    if (this.organization?.name !== '') {
      const organizerName = this.removeEmoji(this.organization?.name);
      return diacritic.clean(serializer(organizerName));
    } else {
      return this.organization?.name;
    }
  }

  public get organizerPageUrl(): string {
    return this.localizeRouterService
      .translateRoute(
        `/attendee.organizer/${this.organization.identifier}/${this.organizationNameFormatted}/attendee.activities/`
      )
      .toString();
  }

  public get organizerProviderShowProfileLink(): string {
    return 'ONEPARTNER';
  }

  public get organizerReviewsTranslation(): string {
    return this.organizationReviewsStats?.count > 1
      ? this.translateService.instant(
          'attendee.activity_detail.organizer.reviews.count',
          {
            organizerReviewsCount: this.organizationReviewsStats?.count || 0,
          }
        )
      : this.translateService.instant(
          'attendee.activity_detail.organizer.review.count',
          {
            organizerReviewsCount: this.organizationReviewsStats?.count || 0,
          }
        );
  }

  public get picture(): string {
    const activityMediaResolution = this.isMobileDevice
      ? ActivityMediaResolutionEnum.CARD
      : ActivityMediaResolutionEnum.LARGE;
    return (
      this.activity?.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        activityMediaResolution
      ) || this.defaultPicture
    );
  }

  public get seeButtonTranslation(): string {
    return this.isOpen
      ? this.translateService.instant(
          'attendee.activity_page.organizer_description.showless'
        )
      : this.translateService.instant(
          'attendee.activity_page.organizer_description.showmore'
        );
  }

  public anchorHandler(event: MouseEvent): void {
    event.preventDefault();
    this.viewportScroller.scrollToAnchor('organization-section');
  }

  private getReviews(): void {
    const reviewsParams = { range: '0-4' };

    this.activityService
      .getActivityReviews(this.activity?.sku, reviewsParams)
      .subscribe((reviews) => {
        this.reviews = reviews;
        this.hasReview = !!this.reviews.length;

        this.activityService
          .getActivityReviewsStats(this.activity?.sku)
          .subscribe((stats) => {
            this.stats = stats;
            this.reviewCount = stats?.count || 0;
            this.averageAttributeRating = stats?.averageAttributeRating;
            this.averageRating = stats?.averageRating || 0;
            this.recommendationRate =
              stats?.recommendedCount / this?.reviewCount || 0;
          });
      });
  }

  public initMap(): void {
    this.mapsService
      .mapScriptInitialized()
      .pipe(take(1))
      .subscribe((initialized) => {
        if (initialized) {
          this.mapsService.initMap(
            this.activity?.location.geo.latitude,
            this.activity?.location.geo.longitude
          );
        }
      });
  }

  public isIntersecting(status: boolean) {
    if (status === true) {
      if (!this.activity?.isLive) {
        this.mapsService.initMapScript();
        this.initMap();
      }
    }
  }

  public isReviewsIntersecting(status: boolean) {
    if (status === true) {
      this.hasScrolled = true;
      this.getReviews();
    }
  }

  public seeMore(): void {
    this.isOpen = !this.isOpen;
  }

  public storeMessagingData(): void {
    this.messagingService.storeRecipient({
      identifier: this.activity.organizerId,
      name: this.organization?.name,
    });
    // Build and store activity data in local storage for talkjs
    const activityData = this.messagingService.buildActivityData(this.activity);
    this.messagingService.storeActivityData(activityData);
    this.messagingService.isRecipientUpdated.next(true);
    this.redirectionService.redirect('/attendee.messages.inbox');
  }
}
